/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */
// Plugins
import { registerPlugins } from '@/plugins';
import { v4 as uuidv4 } from 'uuid';

import i18n from '@/plugins/i18n';
// import '@/styles/app.css'
import '@/styles/global.scss';

// Components
import App from '@/App.vue';
// Composables
import { createApp } from 'vue';
import axios from 'axios';

import { useUserStore } from '@/stores/user';
import { useAppStore } from '@/stores/app';
import ErrorService from '@/services/errorService';
import { AppError } from '@/errors/appError';
import authService from '@/services/authService';
import XTooltipDirective from '@/directives/v-xtooltip';
import { initXTooltip } from '@/helpers/xtooltip';
import { globals } from '@/globals';
import { failMessage, successMessage } from '@/helpers/message';
import { getWebSocketURL } from '@/helpers';
import { useMenuStore } from '@/stores/menu';

document.title = import.meta.env.VITE_APP_NAME;

const app = createApp(App);

app.config.errorHandler = (err, instance, info) => {
    if (err instanceof AppError) {
        ErrorService.log({ err, info });
    } else {
        console.error('[ErrorService]', err);
    }
};

window.addEventListener('error', (event) => {
    ErrorService.log(event.error);

    event.preventDefault();
});

window.addEventListener('unhandledrejection', (event) => {
    ErrorService.log(event.reason);

    event.preventDefault();
});

registerPlugins(app);

const userStore = useUserStore();
const appStore = useAppStore();
const menuStore = useMenuStore();

const uuid = uuidv4();
appStore.uniqAppId = uuid;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

axios.interceptors.request.use(
    (request) => {
        /*request.paramsSerializer = {
          indexes: true
        };*/

        const token = userStore.token;
        if (token) {
            request.headers['Authorization'] = 'Bearer ' + token;
        }

        if (!request.params) {
            request.params = {};
        }

        request.params['uniqAppId'] = appStore.uniqAppId;
        request.params['locale'] = window.localStorage.getItem('locale');

        request.params.filter = JSON.stringify(request.params.filter);

        return request;
    },
    (error) => {
        Promise.reject(error);
    }
);

axios.interceptors.response.use(
    (response) => {
        let prevUrl = response.config.url;
        if (
            prevUrl &&
            response.data.message &&
            !(
                prevUrl.includes('authenticate') ||
                prevUrl.includes('registration') ||
                prevUrl.includes('verify') ||
                prevUrl.includes('verify-email') ||
                prevUrl.includes('reset-password') ||
                prevUrl.includes('forgot-password') ||
                prevUrl.includes('logout') ||
                prevUrl.includes('uploadDocuments')
            )
        ) {
            let message;
            try {
                message = JSON.parse(response.data.message);
                for (let key in message[1]) {
                    message[1][key] = i18n.global.te(message[1][key]) ? i18n.global.t(message[1][key]) : message[1][key];
                }
            } catch (e) {
                message = response.data.message;
            }

            let msg = Array.isArray(message) ? i18n.global.t(message[0], message[1]) : i18n.global.t(message);

            response.data.success ? successMessage(msg) : failMessage(msg);
        }

        return response;
    },
    (error) => {
        if (!error.response) {
            failMessage('Network error');
            ErrorService.log(error);
            return Promise.reject(error);
        }

        const { status, statusText, data, config } = error.response;

        switch (status) {
            case 401:
            case 419:
                authService.logout();
                break;
            case 403:
                if (data.message) {
                    const t = i18n.global.t;
                    failMessage(t(data.message));
                } else {
                    failMessage(statusText);
                }
                break;
            case 404:
            case 500:
                failMessage(statusText);
                break;
            default:
                failMessage(`${status}: ${statusText}`);
                break;
        }

        ErrorService.log({
            url: config?.url,
            method: config?.method,
            status,
            data,
            error,
        });

        return Promise.reject(error.response.data);
    }
);

app.mount('#app');

const token = userStore.token;

//need to use globals to solve issue with vite HMR updates
globals.$connect = app.config.globalProperties.$connect;
globals.$disconnect = app.config.globalProperties.$disconnect;

if (token) {
    const webSocketUrl = getWebSocketURL(token);
    app.config.globalProperties.$connect(webSocketUrl);

    menuStore.withMenu = true;
}

app.directive('xtooltip', XTooltipDirective);
initXTooltip();
